// Small presentational primitives shared by the dashboard. Everything uses Tailwind
// `dark:` variants so nothing vanishes when the `dark` class is on <html>.
import type { ReactNode } from "react";
import type { HealthStatus } from "../lib/api";

// ── Card ─────────────────────────────────────────────────────────────
export function Card({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={
        "rounded-xl border border-gray-200 bg-white p-4 shadow-sm " +
        "dark:border-gray-700 dark:bg-gray-800 " +
        className
      }
    >
      {children}
    </div>
  );
}

export function SectionTitle({ children }: { children: ReactNode }) {
  return (
    <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {children}
    </h2>
  );
}

// ── Status mapping ───────────────────────────────────────────────────
/** Map a raw health string (ok|degraded|down) to the 3-state model. */
export function normalizeHealth(raw: string): HealthStatus {
  if (raw === "down" || raw === "error") return "error";
  if (raw === "degraded" || raw === "warning") return "warning";
  return "ok";
}

export const STATUS_EMOJI: Record<HealthStatus, string> = {
  ok: "🟢",
  warning: "🟡",
  error: "🔴",
};

const STATUS_PILL: Record<HealthStatus, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

export const STATUS_BANNER: Record<HealthStatus, string> = {
  ok: "bg-green-600 text-white",
  warning: "bg-amber-500 text-white",
  error: "bg-red-600 text-white",
};

const STATUS_WORD: Record<HealthStatus, string> = {
  ok: "Operational",
  warning: "Degraded",
  error: "Down",
};

export function StatusPill({ status, label }: { status: HealthStatus; label?: string }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium " +
        STATUS_PILL[status]
      }
    >
      <span aria-hidden>{STATUS_EMOJI[status]}</span>
      {label ?? STATUS_WORD[status]}
    </span>
  );
}

// ── Progress bar (quota usage) ───────────────────────────────────────
/** Color a fraction by quota band: green <80%, amber 80–99%, red ≥100%. */
export function quotaColor(fraction: number): string {
  if (fraction >= 1) return "bg-red-500";
  if (fraction >= 0.8) return "bg-amber-500";
  return "bg-green-500";
}

export function ProgressBar({ fraction }: { fraction: number }) {
  const widthPct = Math.min(100, Math.max(0, fraction * 100));
  return (
    <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
      <div
        className={"h-full rounded-full transition-all " + quotaColor(fraction)}
        style={{ width: `${widthPct}%` }}
        role="progressbar"
        aria-valuenow={Math.round(fraction * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
      />
    </div>
  );
}

// ── Tinted banner (overall health) ───────────────────────────────────
/** Soft tinted surfaces + dot/text colors per status (NOT solid fill). */
const BANNER_TINT: Record<HealthStatus, string> = {
  ok: "bg-green-50 dark:bg-green-900/20",
  warning: "bg-amber-50 dark:bg-amber-900/20",
  error: "bg-red-50 dark:bg-red-900/20",
};

const DOT_FILL: Record<HealthStatus, string> = {
  ok: "bg-green-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
};

const TONE_TEXT: Record<HealthStatus, string> = {
  ok: "text-green-700 dark:text-green-300",
  warning: "text-amber-700 dark:text-amber-300",
  error: "text-red-700 dark:text-red-300",
};

/** Tailwind text-color class for a status (exported for ad-hoc tinting). */
export function toneText(status: HealthStatus): string {
  return TONE_TEXT[status];
}

/** A small status dot (colored circle). */
export function StatusDot({ status, className = "" }: { status: HealthStatus; className?: string }) {
  return (
    <span
      aria-hidden
      className={"inline-block h-2.5 w-2.5 rounded-full " + DOT_FILL[status] + " " + className}
    />
  );
}

/** Tinted overall-health banner: soft bg + colored dot + bold title + muted detail. */
export function Banner({
  status,
  title,
  detail,
}: {
  status: HealthStatus;
  title: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <div className={"flex items-center gap-2.5 rounded-xl px-4 py-3 " + BANNER_TINT[status]}>
      <StatusDot status={status} className="h-3 w-3 shrink-0" />
      <b className={TONE_TEXT[status]}>{title}</b>
      {detail != null && (
        <span className="text-sm text-gray-500 dark:text-gray-400">— {detail}</span>
      )}
    </div>
  );
}

// ── Status pill with explicit dot (mockup style) ─────────────────────
const PILL_TINT: Record<HealthStatus, string> = {
  ok: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  warning: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

/** Pill with a colored dot (instead of emoji) — matches the mockup health cards. */
export function StatusPillDot({ status, label }: { status: HealthStatus; label?: string }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold " +
        PILL_TINT[status]
      }
    >
      <StatusDot status={status} className="h-2 w-2" />
      {label ?? STATUS_WORD[status]}
    </span>
  );
}

// ── Left-border service card ─────────────────────────────────────────
const LEFT_BORDER: Record<HealthStatus, string> = {
  ok: "border-l-green-500",
  warning: "border-l-amber-500",
  error: "border-l-red-500",
};

/** Card with a 3px colored left border keyed to status. Clickable when onClick given. */
export function ServiceBorderCard({
  status,
  onClick,
  children,
}: {
  status: HealthStatus;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <div
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      className={
        "rounded-xl border border-l-[3px] border-gray-200 bg-white p-4 shadow-sm " +
        "dark:border-gray-700 dark:bg-gray-800 " +
        LEFT_BORDER[status] +
        (onClick ? " cursor-pointer transition hover:shadow-md" : "")
      }
    >
      {children}
    </div>
  );
}

// ── Budget bar (horizontal, with 50/80/100% ticks) ───────────────────
/** Bar fill color by projected fraction: green <80%, amber <100%, red ≥100%. */
function budgetFillColor(fraction: number): string {
  if (fraction >= 1) return "bg-red-500";
  if (fraction >= 0.8) return "bg-amber-500";
  return "bg-green-500";
}

export function BudgetBar({
  fraction,
  capUsd,
  projectedUsd,
}: {
  fraction: number;
  capUsd: number;
  projectedUsd: number;
}) {
  const widthPct = Math.min(100, Math.max(0, fraction * 100));
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-700 dark:text-gray-200">
          Monthly budget
        </span>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          projected{" "}
          <b className="text-gray-700 dark:text-gray-200">
            {projectedUsd.toLocaleString("en-US", { style: "currency", currency: "USD" })}
          </b>{" "}
          / cap{" "}
          <b className="text-gray-700 dark:text-gray-200">
            {capUsd.toLocaleString("en-US", { style: "currency", currency: "USD" })}
          </b>
        </span>
      </div>
      <div className="relative h-3.5 w-full overflow-visible rounded-full bg-gray-200 dark:bg-gray-700">
        <div
          className={"h-full rounded-full transition-all " + budgetFillColor(fraction)}
          style={{ width: `${widthPct}%` }}
          role="progressbar"
          aria-valuenow={Math.round(fraction * 100)}
          aria-valuemin={0}
          aria-valuemax={100}
        />
        {/* Tick marks at 50% / 80% / 100% of cap */}
        {[50, 80, 100].map((t) => (
          <span
            key={t}
            aria-hidden
            className="absolute -top-1 h-[22px] w-px bg-gray-400 dark:bg-gray-500"
            style={{ left: `${t}%` }}
          />
        ))}
      </div>
      <div className="mt-1 flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
        <span>50%</span>
        <span>80% warn</span>
        <span>100% cap</span>
      </div>
    </div>
  );
}
